<template>
  <el-drawer
    :title="title"
    :visible.sync="innerVisible"
    direction="rtl"
    size="960px"
    append-to-body>
    <div v-if="detail" class="detail-panel" v-loading="loading">
      <el-tabs v-model="innerTab" type="border-card">
        <el-tab-pane label="基本信息" name="info">
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="订单号">
              {{ order.orderNo }}
              <source-badge :source="order.source" />
            </el-descriptions-item>
            <el-descriptions-item label="结算类型">
              {{ order.settlementType === 'LABOR' ? '劳务结算' : '供应商结算' }}
            </el-descriptions-item>
            <el-descriptions-item label="所属机构">{{ order.orgName || order.orgId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="关联项目">{{ order.projectName || order.projectId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="关联会议">{{ order.meetingName || order.meetingId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="结算模式">{{ modeLabel(order.settlementMode) }}</el-descriptions-item>
            <el-descriptions-item label="供应商">{{ order.supplierOrgName || order.supplierName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="采购方">{{ order.buyerOrgName || order.buyerName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="收款方式">{{ order.collectWay || '-' }}</el-descriptions-item>
            <el-descriptions-item label="订单金额">
              <span class="amount">¥{{ Number(order.orderAmount || 0).toFixed(2) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="主状态">
              <el-tag size="mini" :type="statusType(order.status)">{{ statusLabel(order.status) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="当前阶段">{{ stageLabel(order.currentStage) }}</el-descriptions-item>
            <el-descriptions-item label="备注" :span="2">{{ order.remark || '-' }}</el-descriptions-item>
          </el-descriptions>

          <div class="action-bar">
            <el-button v-if="canSubmit" type="success" size="small" icon="el-icon-s-promotion" @click="doSubmit">
              提交审核 (将触发 AI 审核)
            </el-button>
            <el-button size="small" icon="el-icon-cpu" @click="$emit('ai-audit', order)">
              查看 AI 审核结果
            </el-button>
          </div>
        </el-tab-pane>

        <el-tab-pane :label="`费用明细 (${(detail.items || []).length})`" name="items">
          <div class="section-tools">
            <el-button v-if="canEdit" type="primary" size="mini" icon="el-icon-plus" @click="openAddItem">
              添加明细
            </el-button>
          </div>
          <el-table :data="detail.items || []" size="mini" border>
            <el-table-column prop="feeType" label="费用类型" width="160" />
            <el-table-column prop="amount" label="金额" width="120" align="right">
              <template slot-scope="{ row }">¥{{ Number(row.amount || 0).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column prop="remark" label="备注" min-width="180" />
            <el-table-column label="操作" width="80" align="center">
              <template slot-scope="{ row }">
                <el-button v-if="canEdit" type="text" size="mini" style="color:#F56C6C" @click="doRemoveItem(row)">
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane :label="`材料文件 (${(detail.folders || []).length})`" name="folders">
          <div class="section-tools">
            <el-button v-if="canEdit" type="primary" size="mini" icon="el-icon-upload2" @click="openAddFolder">
              添加材料
            </el-button>
          </div>
          <el-table :data="detail.folders || []" size="mini" border>
            <el-table-column prop="feeType" label="费用类型" width="120" />
            <el-table-column prop="docType" label="文档类型" width="120" />
            <el-table-column prop="fileName" label="文件名" min-width="180" show-overflow-tooltip />
            <el-table-column prop="ocrStatus" label="OCR" width="80" align="center">
              <template slot-scope="{ row }">
                <el-tag size="mini" :type="ocrType(row.ocrStatus)">{{ row.ocrStatus || '-' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="审核状态" width="160" align="center">
              <template slot-scope="{ row }">
                <el-tag size="mini" :type="folderStatusType(row.status)">{{ folderStatusLabel(row.status) }}</el-tag>
                <el-tooltip v-if="row.status === 'NEEDS_REUPLOAD' && row.issueReason"
                            :content="row.issueReason" placement="top">
                  <i class="el-icon-warning" style="color:#E6A23C;margin-left:4px"></i>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120" align="center">
              <template slot-scope="{ row }">
                <el-button v-if="canMarkIssue && row.status !== 'NEEDS_REUPLOAD'"
                           type="text" size="mini" style="color:#E6A23C"
                           @click="openMarkIssue(row)">
                  标记需重传
                </el-button>
                <span v-else-if="row.status === 'NEEDS_REUPLOAD'" style="color:#909399;font-size:12px">已标记</span>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="审核记录" name="audit">
          <audit-timeline :order-id="order.id" />
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 加费用明细 -->
    <el-dialog title="添加费用明细" :visible.sync="itemDialogVisible" width="500px" append-to-body>
      <el-form :model="itemForm" label-width="100px" size="small">
        <el-form-item label="费用类型" required>
          <el-select v-model="itemForm.feeType" filterable clearable allow-create style="width:100%">
            <el-option v-for="o in feeTypeOptions" :key="o.id" :label="o.dictLabel" :value="o.dictLabel" />
          </el-select>
        </el-form-item>
        <el-form-item label="金额" required>
          <el-input-number v-model="itemForm.amount" :min="0" :precision="2" :step="100" style="width:100%" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="itemForm.remark" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="itemDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="itemSaving" @click="doAddItem">保存</el-button>
      </div>
    </el-dialog>

    <!-- G7: 标记需重传 -->
    <el-dialog title="标记需重传" :visible.sync="markIssueVisible" width="480px" append-to-body>
      <div v-if="markIssueFolder" class="mark-issue-info">
        <p><strong>文件:</strong> {{ markIssueFolder.docType }} - {{ markIssueFolder.fileName }}</p>
      </div>
      <el-input v-model="markIssueReason" type="textarea" :rows="3"
                placeholder="请填写需重传原因(例如: 发票模糊、金额错误...)" />
      <div slot="footer">
        <el-button size="small" @click="markIssueVisible = false">取消</el-button>
        <el-button size="small" type="warning" :loading="markIssueSaving" @click="doMarkIssue">
          确认标记
        </el-button>
      </div>
    </el-dialog>

    <!-- 加材料 -->
    <el-dialog title="添加材料文件" :visible.sync="folderDialogVisible" width="500px" append-to-body>
      <el-form :model="folderForm" label-width="100px" size="small">
        <el-form-item label="费用类型">
          <el-select v-model="folderForm.feeType" filterable clearable allow-create style="width:100%">
            <el-option v-for="o in feeTypeOptions" :key="o.id" :label="o.dictLabel" :value="o.dictLabel" />
          </el-select>
        </el-form-item>
        <el-form-item label="文档类型" required>
          <el-select v-model="folderForm.docType" filterable clearable allow-create style="width:100%">
            <el-option v-for="o in docTypeOptions" :key="o.id" :label="o.dictLabel" :value="o.dictLabel" />
          </el-select>
        </el-form-item>
        <el-form-item label="文件 ID">
          <el-input-number v-model="folderForm.fileId" :min="0" style="width:100%" />
          <div class="form-hint">文件上传完成后会得到文件 ID,此处填该 ID 关联</div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="folderDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="folderSaving" @click="doAddFolder">保存</el-button>
      </div>
    </el-dialog>
  </el-drawer>
</template>

<script>
import {
  getSpendingOrderDetail, submitSpendingOrder,
  addSpendingItem, removeSpendingItem, addSpendingFolder,
  markSpendingFolderIssue
} from '@/request/api.js';
import { aiDictApi } from '@/views/type_assistant/z_aiaudit/api.js';
import AuditTimeline from './AuditTimeline.vue';
import SourceBadge from '@/components/SourceBadge.vue';

const STATUS_TYPE = {
  DRAFT: 'info', SUBMITTED: '', AI_AUDITING: 'warning', MANUAL_AUDITING: 'warning',
  APPROVED: 'success', REJECTED: 'danger', SETTLED: 'success'
};
const STATUS_LABEL = {
  DRAFT: '草稿', SUBMITTED: '已提交', AI_AUDITING: 'AI 审核中', MANUAL_AUDITING: '人工审核中',
  APPROVED: '已通过', REJECTED: '已驳回', SETTLED: '已结算'
};
const STAGE_LABEL = {
  SUPPLIER: '供应商', AI: 'AI 审核', OFFICIAL: '官员审核', FINANCE: '财务审核', DONE: '已完成'
};
const MODE_LABEL = {
  PROJECT: '按项目结算', BATCH: '按 N 场会结算', PER_MEETING: '每场会单结'
};

export default {
  name: 'SpendingOrderDetail',
  components: { AuditTimeline, SourceBadge },
  props: {
    visible: { type: Boolean, default: false },
    orderId: { type: [Number, String], default: null }
  },
  data() {
    return {
      detail: null, loading: false, innerTab: 'info',
      feeTypeOptions: [], docTypeOptions: [],
      itemDialogVisible: false, itemSaving: false,
      itemForm: { feeType: '', amount: 0, remark: '' },
      folderDialogVisible: false, folderSaving: false,
      folderForm: { feeType: '', docType: '', fileId: 0 },
      // G7: 单文件级驳回
      markIssueVisible: false, markIssueSaving: false,
      markIssueFolder: null, markIssueReason: ''
    };
  },
  computed: {
    innerVisible: {
      get() { return this.visible; },
      set(v) { this.$emit('update:visible', v); }
    },
    order() { return (this.detail && this.detail.order) || {}; },
    title() { return this.order.orderNo ? `结算单 - ${this.order.orderNo}` : '结算单详情'; },
    canEdit() { return ['DRAFT', 'REJECTED', 'COLLECTING_MATERIALS'].indexOf(this.order.status) >= 0; },
    canSubmit() { return ['DRAFT', 'REJECTED', 'COLLECTING_MATERIALS'].indexOf(this.order.status) >= 0; },
    // G7: 仅 OFFICIAL_AUDIT / ADMIN 在审核阶段可标记文件
    canMarkIssue() {
      const role = JSON.parse(localStorage.getItem('token') || '{}').type;
      const auditStatuses = ['MANUAL_AUDITING', 'AI_AUDITING'];
      return ['ADMIN', 'OFFICIAL_AUDIT'].includes(role) && auditStatuses.includes(this.order.status);
    }
  },
  watch: {
    orderId: { immediate: true, handler(v) { if (v) this.load(); } },
    visible(v) { if (v) this.loadDicts(); }
  },
  methods: {
    statusType(s) { return STATUS_TYPE[s] || 'info'; },
    statusLabel(s) { return STATUS_LABEL[s] || s || '-'; },
    stageLabel(s) { return STAGE_LABEL[s] || s || '-'; },
    modeLabel(m) { return MODE_LABEL[m] || m || '-'; },
    ocrType(s) {
      if (s === 'SUCCESS') return 'success';
      if (s === 'FAILED') return 'danger';
      if (s === 'PENDING' || s === 'RUNNING') return 'warning';
      return 'info';
    },
    folderStatusLabel(s) {
      const m = {
        PENDING: '待审核', AI_PASSED: 'AI 通过', AI_FAILED: 'AI 拒绝',
        MANUAL_OVERRIDDEN: '人工覆盖', NEEDS_REUPLOAD: '需重传', REPLACED: '已替换'
      };
      return m[s] || s || '-';
    },
    folderStatusType(s) {
      if (s === 'AI_PASSED' || s === 'MANUAL_OVERRIDDEN') return 'success';
      if (s === 'AI_FAILED' || s === 'NEEDS_REUPLOAD') return 'warning';
      if (s === 'REPLACED') return 'info';
      return '';
    },

    // G7: 标记需重传
    openMarkIssue(row) {
      this.markIssueFolder = row;
      this.markIssueReason = '';
      this.markIssueVisible = true;
    },
    async doMarkIssue() {
      if (!this.markIssueReason || !this.markIssueReason.trim()) {
        return this.$message.warning('请填写需重传原因');
      }
      this.markIssueSaving = true;
      try {
        const resp = await markSpendingFolderIssue(this.markIssueFolder.id, this.markIssueReason.trim());
        if (resp.code === 200) {
          this.$message.success('已标记需重传, 通知 ASSISTANT/上传者');
          this.markIssueVisible = false;
          this.load();
        } else {
          this.$message.error(resp.msg || '标记失败');
        }
      } catch (e) {
        this.$message.error('标记失败');
      } finally {
        this.markIssueSaving = false;
      }
    },

    async load() {
      if (!this.orderId) return;
      this.loading = true;
      try {
        const resp = await getSpendingOrderDetail(this.orderId);
        if (resp.code === 200) this.detail = resp.data;
      } catch (e) {
        this.$message.error('详情加载失败');
      } finally {
        this.loading = false;
      }
    },
    async loadDicts() {
      try {
        const [fee, doc] = await Promise.all([
          aiDictApi.options('fee_type'),
          aiDictApi.options('doc_type')
        ]);
        if (fee.code === 200) this.feeTypeOptions = fee.data || [];
        if (doc.code === 200) this.docTypeOptions = doc.data || [];
      } catch (e) { /* ignore */ }
    },

    async doSubmit() {
      try {
        await this.$confirm('提交后将触发 AI 审核, 确认?', '提示', { type: 'warning' });
        const resp = await submitSpendingOrder(this.order.id);
        if (resp.code === 200) {
          this.$message.success('已提交');
          this.load();
          this.$emit('changed');
        } else this.$message.error(resp.msg || '提交失败');
      } catch (e) { /* cancel */ }
    },

    openAddItem() {
      this.itemForm = { feeType: '', amount: 0, remark: '' };
      this.itemDialogVisible = true;
    },
    async doAddItem() {
      if (!this.itemForm.feeType) return this.$message.warning('请选择费用类型');
      if (!this.itemForm.amount) return this.$message.warning('金额必填');
      this.itemSaving = true;
      try {
        const resp = await addSpendingItem(this.order.id, this.itemForm);
        if (resp.code === 200) {
          this.$message.success('已添加');
          this.itemDialogVisible = false;
          this.load();
        }
      } catch (e) {
        this.$message.error('添加失败');
      } finally { this.itemSaving = false; }
    },
    async doRemoveItem(row) {
      try {
        await this.$confirm('确认删除?', '提示', { type: 'warning' });
        const resp = await removeSpendingItem(row.id);
        if (resp.code === 200) { this.$message.success('已删除'); this.load(); }
      } catch (e) { /* cancel */ }
    },

    openAddFolder() {
      this.folderForm = { feeType: '', docType: '', fileId: 0 };
      this.folderDialogVisible = true;
    },
    async doAddFolder() {
      if (!this.folderForm.docType) return this.$message.warning('请选择文档类型');
      this.folderSaving = true;
      try {
        const resp = await addSpendingFolder(this.order.id, this.folderForm);
        if (resp.code === 200) {
          this.$message.success('已添加');
          this.folderDialogVisible = false;
          this.load();
        }
      } catch (e) {
        this.$message.error('添加失败');
      } finally { this.folderSaving = false; }
    }
  }
};
</script>

<style scoped>
.detail-panel { padding: 12px; }
.amount { color: #E6A23C; font-weight: 600; }
.action-bar { margin-top: 16px; display: flex; gap: 8px; }
.section-tools { margin-bottom: 8px; text-align: right; }
.form-hint { color: #909399; font-size: 12px; }
.mark-issue-info { background: #FFF7E6; padding: 10px 12px; border-radius: 4px; margin-bottom: 12px; color: #6B4F00; font-size: 13px; }
.mark-issue-info p { margin: 0; }
</style>
